// material-ui
import {
  Container,
  Card,
  Typography,
  Box,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  Divider,
  Button
} from '@mui/material';

import { Http } from 'api/http/http';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import StudentPasswordPermute from './StudentPasswordPermute';

// ==============================|| COMPONENTS - TYPOGRAPHY ||============================== //

const splitModulesData = (modulesData) => {
  let issuedModules = [];
  let applyingModules = [];

  modulesData.forEach((module) => {
    if (module.status === 'APPROVED') {
      issuedModules = [...issuedModules, module.moduleName];
    } else if (module.status === 'PENDING') {
      applyingModules = [...applyingModules, module.moduleName];
    }
  });

  return { issuedModules, applyingModules };
};

const splitScholarshipsData = (scholarshipsData) => {
  let issuedScholarships = [];
  let applyingScholarships = [];

  if (scholarshipsData && Array.isArray(scholarshipsData)) {
    scholarshipsData.forEach((scholarship) => {
      if (scholarship.status === '승인' || scholarship.status === '확정') {
        issuedScholarships = [...issuedScholarships, scholarship.moduleName];
      } else if (scholarship.status === '신청중' || scholarship.status === '심사중') {
        applyingScholarships = [...applyingScholarships, scholarship.moduleName];
      }
    });
  }

  return { issuedScholarships, applyingScholarships };
};



const getSubjectInfoList = (allSubjectData, major) => {
  let subjectInfoList = [];

  // /api/course-enrollments API 응답 구조에 맞게 수정
  allSubjectData.forEach((course) => {
    // major 정보에 따라 모듈 필터링
    const isMajorMatch = course.moduleName.some((moduleName) => {
      if (major === 'STEM' && moduleName === '비이공계') {
        return false; // 이공계 학생이면 비이공계 모듈 제외
      }
      if (major !== 'STEM' && moduleName === '이공계') {
        return false; // 비이공계 학생이면 이공계 모듈 제외
      }
      return true;
    });

    if (!isMajorMatch) {
      return; // 해당 전공에 맞지 않는 모듈 제외
    }

    // level / moduleName 배열을 활용해 "모듈명(과정)" 형태 문자열 생성
    const levelMapping = {
      HIGH: '고급',
      MIDDLE: '중급',
      LOW: '초급'
    };

    const levels = Array.isArray(course.level) ? course.level : [];
    const moduleNames = Array.isArray(course.moduleName) ? course.moduleName : [];

    const modulesWithLevel = moduleNames.map((name, idx) => {
      const rawLevel = levels[idx] || levels[0] || '';
      const levelKorean = levelMapping[rawLevel] || rawLevel || '';
      return levelKorean ? `${name}(${levelKorean})` : name;
    });

    subjectInfoList.push({
      courseName: course.courseName, // 개설교과목
      standardCourseName: course.courseName, // 표준교과목 (현재는 동일)
      modulesWithLevel: modulesWithLevel.join(', '),
      isAvailable: course.isAvailable
    });
  });

  return subjectInfoList;
};

const StudentHome = () => {
  const [studentInfo, setStudentInfo] = useState({
    issuedModules: [],
    applyingModules: [],
    issuedScholarships: [],
    applyingScholarships: [],
    subjectInfoList: []
  });

  const [open, setOpen] = useState(false);
  const { name, major } = useSelector((props) => props.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchUserData = useCallback(async () => {
    try {
      // API URL 변경
      const urls = ['/api/certificates', '/api/scholarships', '/api/course-enrollments'];
      const [modulesRawData, scholarshipsRawData, allSubjectRawData] = await Promise.all(urls.map((url) => http.getWithToken(url)));

      const modulesData = modulesRawData.data.result;
      const scholarshipsData = scholarshipsRawData.data.result;
      const allSubjectData = allSubjectRawData.data.result;
      // console.log('subjectInfoList', allSubjectData);

      // major 정보를 전달하여 교과목 정보 생성
      const subjectInfoList = getSubjectInfoList(allSubjectData, major);

      const { issuedModules, applyingModules } = splitModulesData(modulesData);
      // console.log('issuedModules', issuedModules);
      const { issuedScholarships, applyingScholarships } = splitScholarshipsData(scholarshipsData);
      // console.log('issuedScholarships', issuedScholarships);

      setStudentInfo((prev) => ({
        ...prev,
        issuedModules: issuedModules,
        applyingModules: applyingModules,
        issuedScholarships: issuedScholarships,
        applyingScholarships: applyingScholarships,
        subjectInfoList: subjectInfoList
      }));
    } catch (error) {
      console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
    }
  }, [major]); // major 의존성 배열에 추가

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return (
    <Container sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 2 }}>
        <Typography variant="h1">{name}님의 이수 현황</Typography>
        <Box sx={{ marginLeft: 'auto' }}>
          <Button variant="contained" color="primary" size="medium" sx={{ marginLeft: 'auto' }} onClick={handleOpen}>
            비밀번호 변경
          </Button>
          <StudentPasswordPermute open={open} onClose={handleClose} />
        </Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, flexWrap: 'wrap', marginY: 5 }}>
        <Card sx={{ minWidth: 275 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <CardHeader title="발급 받은 이수증" />
            <Typography
              variant="h5"
              sx={{ fontWeight: 'bolder', color: 'primary.main' }}
            >{`${studentInfo.issuedModules.length}건`}</Typography>
          </Box>
          <CardContent>
            <List>
              {studentInfo.issuedModules.length === 0 ? (
                <ListItem>
                  <ListItemText primary={'현재 목록이 비어있습니다.'} />
                </ListItem>
              ) : (
                studentInfo.issuedModules.map((module) => (
                  <ListItem key={module}>
                    <ListItemButton key={module} onClick={() => navigate('/student-certificate-management')}>
                      <ListItemText primary={module} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 275 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <CardHeader title="발급 받은 장학증서" />
            <Typography
              variant="h5"
              sx={{ fontWeight: 'bolder', color: 'primary.main' }}
            >{`${studentInfo.issuedScholarships.length}건`}</Typography>
          </Box>
          <CardContent>
            <List>
              {studentInfo.issuedScholarships.length === 0 ? (
                <ListItem>
                  <ListItemText primary={'현재 목록이 비어있습니다.'} />
                </ListItem>
              ) : (
                studentInfo.issuedScholarships.map((scholarship) => (
                  <ListItem key={scholarship}>
                    <ListItemButton key={module} onClick={() => navigate('/student-scholarship-certificate-management')}>
                      <ListItemText primary={scholarship} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 275 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <CardHeader title="신청 중인 이수증" />
            <Typography
              variant="h5"
              sx={{ fontWeight: 'bolder', color: 'warning.main' }}
            >{`${studentInfo.applyingModules.length}건`}</Typography>
          </Box>
          <CardContent>
            <List>
              {studentInfo.applyingModules.length === 0 ? (
                <ListItem>
                  <ListItemText primary={'현재 목록이 비어있습니다.'} />
                </ListItem>
              ) : (
                studentInfo.applyingModules.map((module) => (
                  <ListItem key={module}>
                    <ListItemButton key={module} onClick={() => navigate('/student-certificate-management')}>
                      <ListItemText primary={module} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 275 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <CardHeader title="신청 중인 장학증서" />
            <Typography
              variant="h5"
              sx={{ fontWeight: 'bolder', color: 'warning.main' }}
            >{`${studentInfo.applyingScholarships.length}건`}</Typography>
          </Box>
          <CardContent>
            {studentInfo.applyingScholarships.length === 0 ? (
              <ListItem>
                <ListItemText primary={'현재 목록이 비어있습니다.'} />
              </ListItem>
            ) : (
              studentInfo.applyingScholarships.map((scholarship) => (
                <ListItem key={scholarship}>
                  <ListItemButton key={module} onClick={() => navigate('/student-scholarship-certificate-management')}>
                    <ListItemText primary={scholarship} />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
      <Paper sx={{ width: '100%%' }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bolder' }} colSpan={6} align="center">
                  마이크로 모듈 교과목 이수 현황
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>개설교과목</TableCell>
                <TableCell>표준교과목</TableCell>
                <TableCell>활용가능모듈</TableCell>
                <TableCell>활용가능여부</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studentInfo.subjectInfoList.length === 0 ? (
                <TableRow>
                  <TableCell sx={{ textAlign: 'center' }} colSpan={4}>
                    현재 목록이 비어있습니다.
                  </TableCell>
                </TableRow>
              ) : (
                studentInfo.subjectInfoList.map((subjectInfo, index) => (
                  <TableRow key={index}>
                    <TableCell>{subjectInfo.courseName}</TableCell>
                    <TableCell>{subjectInfo.standardCourseName}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 260,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}
                    >
                      {subjectInfo.modulesWithLevel}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: subjectInfo.isAvailable ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}
                      >
                        {subjectInfo.isAvailable ? '가능' : '불가능'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default StudentHome;
